import React, { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

// CONTEXT
import { ToastContext } from 'context/ToastContext';
import { AuthContext } from 'context/AuthContext';

// HOOKS
import useUserData from 'hooks/useUserData';

// SERVICES
import { getTilesTypeLink, deleteTileTypeLink } from 'services/tileService';

// COMPONENTS
import TableComponent from 'components/common/tableComponent';
import SearchBar from 'components/administration/searchBarComponent'
import Loader from 'components/loaderComponent';

// ICONS
import { Plus, Edit3, Trash, Check, X } from 'lucide-react';

const ManagementTilesPage = () => {
  const { addToast } = useContext(ToastContext);
  const { email, isInternal, isExternal, isAdmin, loading: authLoading } = useContext(AuthContext);
  const { userBDDInfo, loading: userLoading } = useUserData(email, isInternal, isExternal, isAdmin);
  const [userId, setUserId] = useState(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [initialTilesData, setInitialTilesData] = useState([]);
  const [confirmingDeleteTileId, setConfirmingDeleteTileId] = useState(null);
  const colors = ['EF9700', '003B5E', '00BBBA', 'DB0661'];
  const getRandomColor = () => colors[Math.floor(Math.random() * colors.length)];

  useEffect(() => {
    if (userBDDInfo?.data?.[0]?.USR_ID && !userLoading) {
      setUserId(userBDDInfo.data[0].USR_ID);
    }
  }, [userBDDInfo, userLoading]);

  useEffect(() => {
    const fetchTiles = async () => {
      try {
        const response = await getTilesTypeLink();
        const data = response.data;
        const transformedData = data.map(tile => ({
          id: String(tile.id),
          icon: { 
            src: tile.image_url || `https://placehold.co/40x40/${getRandomColor()}/FFF?text=${tile.titre ? tile.titre.substring(0, 2).toUpperCase() : 'N/A'}`,
            alt: `${tile.titre || 'Tile'} Icon` 
          },
          title: tile.titre,
          description: tile.description,
          url: tile.lien,
          optional: {
            text: tile.facultatif ? 'Oui' : 'Non',
            variant: tile.facultatif ? 'success' : 'neutral',
          },
          requiresVPN: {
            text: tile.requires_vpn === '1' || tile.requires_vpn === true ? 'Oui' : 'Non',
            variant: tile.requires_vpn === '1' || tile.requires_vpn === true ? 'success' : 'neutral',
          },
          availableForPresta: {
            text: tile.available_for_presta === '1' || tile.available_for_presta === true ? 'Oui' : 'Non',
            variant: tile.available_for_presta === '1' || tile.available_for_presta === true ? 'success' : 'neutral',
          },
        }));
        setInitialTilesData(transformedData);
      } catch (error) {
        console.error("Erreur lors de la récupération des tuiles:", error);
        setInitialTilesData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTiles();
  }, []);

  const handleEdit = (item) => {
    // Sauvegarder les données de la tuile dans le localStorage
    localStorage.setItem('tileFormData', JSON.stringify({
      tileId: item.id,
      titre: item.title,
      description: item.description,
      lien: item.url,
      facultatif: item.optional.text === 'Oui',
      requiresVPN: item.requiresVPN.text === 'Oui',
      availableForPresta: item.availableForPresta.text === 'Oui',
      image_url: item.icon.src.includes('placehold.co') ? null : item.icon.src
    }));
    // Rediriger vers le formulaire
    navigate('/admin/tiles/management/form');
  };

  const handleDelete = async (item) => {
    setConfirmingDeleteTileId(item.id);
  };

  const executeConfirmedDelete = async (item) => {
    const response = await deleteTileTypeLink(item.id, userId);
    if (response.status === 200) {
      setInitialTilesData(prevTiles => prevTiles.filter(tile => tile.id !== item.id));
      setConfirmingDeleteTileId(null);
      addToast('Suppression réussie', 'La tuile a été supprimée avec succès', null, null, 'success');
    } else {
      console.error("Erreur lors de la suppression de la tuile:", response.data);
      addToast('Erreur lors de la suppression de la tuile', 'Une erreur est survenue lors de la suppression de la tuile', null, null, 'error');
    }
  };

  const cancelDelete = () => {
    setConfirmingDeleteTileId(null);
  };

  const handleAddNew = () => {
    // Nettoyer le localStorage avant d'ajouter une nouvelle tuile
    localStorage.removeItem('tileFormData');
    // Rediriger vers le formulaire
    navigate('/admin/tiles/management/form');
  };

  const tilesDataWithActions = useMemo(() => initialTilesData.map(tile => {
    const isConfirming = confirmingDeleteTileId === tile.id;
    let actions = [];

    if (isConfirming) {
      actions = [
        { icon: Check, onClick: () => executeConfirmedDelete(tile), variant: 'success', label: 'Confirmer la suppression' },
        { icon: X, onClick: cancelDelete, variant: 'error', label: 'Annuler la suppression' }
      ];
    } else {
      actions = [
        { icon: Edit3, onClick: () => handleEdit(tile), variant: 'info', label: 'Modifier' }
      ];
      // N'ajouter le bouton de suppression que si la tuile est facultative
      if (tile.optional.text === 'Oui') {
        actions.push({ icon: Trash, onClick: () => handleDelete(tile), variant: 'error', label: 'Supprimer' });
      }
    }
    return {
      ...tile,
      actions
    };
  }), [initialTilesData, confirmingDeleteTileId]);

  const filteredTiles = useMemo(() => {
    if (!searchTerm) {
      return tilesDataWithActions;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return tilesDataWithActions.filter(
      (tile) =>
        tile.title.toLowerCase().includes(lowerSearchTerm) ||
        (tile.description && tile.description.toLowerCase().includes(lowerSearchTerm)) ||
        (tile.url && tile.url.toLowerCase().includes(lowerSearchTerm))
    );
  }, [searchTerm, tilesDataWithActions]);

  const columns = useMemo(() => [
    { key: 'icon', label: 'Icône', type: 'image' },
    { key: 'title', label: 'Titre', type: 'text' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'url', label: 'Lien', type: 'link' },
    { key: 'optional', label: 'Facultatif', type: 'badge' },
    { key: 'requiresVPN', label: 'Nécessite VPN', type: 'badge' },
    { key: 'availableForPresta', label: 'Disponible pour prestataire', type: 'badge' },
    { key: 'actions', label: 'Actions', type: 'action' },
  ], []);

  if (isLoading || authLoading || userLoading || !userId) {
    return <Loader />;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <SearchBar 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        <button
          className="w-full sm:w-auto flex items-center justify-center px-4 py-2 text-blanc rounded-lg shadow-md bg-orange hover:bg-framboise transition-colors ease-in-out duration-300"
          onClick={handleAddNew}
        >
          <Plus className="mr-2" />
          Ajouter une tuile
        </button>
      </div>

      <div className="flex-1 min-h-0">
        <TableComponent 
          columns={columns}
          rows={filteredTiles}
          enablePagination={true}
          defaultPageSize={10}
          pageSizeOptions={[10, 25, 50, 100]}
          emptyText="Aucune tuile trouvée." />
      </div>
    </div>
  );
};

export default ManagementTilesPage;